import { Mail, Phone, Globe } from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faLinkedinIn } from "@fortawesome/free-brands-svg-icons";

import { ShareArticleSection } from "../ShareArticleSection";

const CXPerspectives = () => {
  return (
    <div className="modal-content-body">
      <ShareArticleSection />
      <div className="modal-section">
        <div className="flex-content">
          <div>
            <h3>Use Case</h3>
          </div>
          <div>
            <p>
              Unlock actionable intelligence and real-time analytics from your
              Amazon Connect ecosystem. Quantify customer sentiment, friction,
              and agent performance to drive measurable CX improvement.
            </p>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Solution</h3>
          </div>
          <div>
            <a
              href="https://aws.amazon.com/marketplace/pp/prodview-czkuxmj5znzwi?sr=0-6&ref_=beagle&applicationId=AWSMPContessa"
              target="_blank"
              rel="noopener noreferrer"
            >
              CX Perspectives
            </a>{" "}
            (Enables Quicksight, NeptuneDB, Contact Lens, customisable
            dashboards & AI analytics)
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Segment</h3>
          </div>
          <div>
            <ul>
              <li>
                Organisations running any contact centre platform who want more
                than basic reporting - providing an insight into the Art of the
                Possible with AWS.
              </li>
              <li>
                Enterprise and SMBs seeking advanced, AI-enhanced insights and
                journey analytics.
              </li>
              <li>
                Businesses wanting to benchmark, optimise, and continuously
                improve CX using data.
              </li>
            </ul>
          </div>
        </div>

        <div className="flex-content">
          <div>
            <h3>Target Personas</h3>
          </div>
          <div>
            <p>
              Head of CX, Chief Data/Analytics Officer, Contact Centre Manager,
              Operations Lead, BI Team
            </p>
          </div>
        </div>
      </div>

      {/* Opportunity & Qualification */}
      <div className="modal-section">
        <h2 className="modal-section-title">Opportunity and Qualification</h2>
        <div className="modal-section-grid">
          <div className="modal-column">
            <h3 className="modal-subtitle">
              What is the customer trying to accomplish?
            </h3>
            <ul>
              <li>
                Identify emerging trends, intents, and friction in real time.
              </li>
              <li>
                Coach, recognise, and upskill agents using actionable insights.
              </li>
              <li>Surface and resolve process, product, or training gaps.</li>
              <li>Build business cases for CX change and rapid ROI.</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Key Words/ Questions</h3>
            <ul>
              <li>
                Want better visibility on customer journeys or service
                bottlenecks.
              </li>
              <li>
                Data is siloed across channels or too basic to drive change.
              </li>
              <li>Reporting takes too long or misses key trends/risks.</li>
              <li>Struggling with compliance or agent coaching consistency.</li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Pain Point/ Challenge</h3>
            <ul>
              <li>Blind spots in customer experience or root causes.</li>
              <li>Slow or manual reporting and QA processes.</li>
              <li>Lack of actionable views by intent, channel, or agent.</li>
              <li>
                Inability to quantify impact of CX investments in real time.
              </li>
            </ul>
          </div>

          <div className="modal-column">
            <h3 className="modal-subtitle">Qualifying Questions</h3>
            <ul>
              <li>
                Are you able to see and act on new customer issues or friction
                points as they emerge?
              </li>
              <li>
                Would agent- and intent-level insight make QA, training, or
                compliance easier?
              </li>
              <li>
                Do you need to build clear business cases for CX technology or
                process changes?
              </li>
              <li>
                Is your current reporting too slow or not actionable enough?
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Proposed Solution */}
      <div className="modal-section">
        <h2 className="modal-section-title">Solution Description</h2>
        <div className="flex-content-half">
          <div>
            <h3>Solution Description</h3>
            <ul style={{ marginTop: "10px" }}>
              <li>
                Rapid integration with Amazon Connect, Contact Lens, Quicksight,
                and Athena.
              </li>
              <li>
                Pre-built and custom dashboards: intent, sentiment, channel,
                agent, QA.
              </li>
              <li>
                AI-enabled analytics surface hidden risks, upsides, and process
                gaps.
              </li>
              <li>
                Managed service includes advisory, continuous fine-tuning, and
                best practice reviews.
              </li>
            </ul>
          </div>
          <div>
            <h3>Solution Benefits to AWS Customers</h3>
            <ul style={{ marginTop: "10px" }}>
              <li>
                Real-time, action-ready insights to optimise customer journeys.
              </li>
              <li>
                Evidence for business cases and ROI on every CX improvement.
              </li>
              <li>
                Industry-leading analytics fully tailored to each business's
                needs.
              </li>
              <li>
                Dramatically improved agent coaching, compliance, and customer
                satisfaction.
              </li>
            </ul>
          </div>
        </div>

        <h2 className="modal-section-title" style={{ marginTop: "30px" }}>
          Objection Handling
        </h2>
        <div className="flex-content-half">
          <div>
            <p>
              <b>We already have dashboards:</b> CX Perspectives delivers
              real-time, journey-level, and AI-driven insight, not basic KPIs.
            </p>

            <p>
              <b>Implementation will be complex:</b> Rapid, AWS-native setup
              with dedicated support and advisory.
            </p>

            <p>
              <b>Our data is siloed:</b> Full integration for a single view
              across all channels and sources.
            </p>
            <p>
              <b>No time for ongoing analysis:</b> Managed service means iCXeed
              provides insight, not homework.
            </p>
            <p>
              <b>ROI is unclear:</b> Business case builder evidences and
              quantifies every change.
            </p>
          </div>
        </div>
      </div>

      <div className="modal-footnote" style={{ border: 0 }}>
        <h3>Primary Partner Contact:</h3>
        <div className="flex-box-blk">
          <div>
            <img
              src="https://www.icxeed.ai/wp-content/themes/icxeed-website-2025/portal-assets/images/thumbnails/ryan-thumbnail.png"
              alt="ryan rayner"
            />
          </div>
          <div>
            <h2>Ryan Rayner</h2>
            <p>Co-Founder & Chief Customer Officer</p>
          </div>
        </div>
        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <Mail size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="mailto:ryan.rayner@icxeed.ai">ryan.rayner@icxeed.ai</a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Phone size={20} color="#01C2F3" />
            </div>
            <div>
              <a href="tel:+61438286198">+61 438 286 198</a>
            </div>
          </div>
        </div>

        <div className="flex-box-blk footnote-flex-box">
          <div className="flex-box-blk">
            <div>
              <FontAwesomeIcon icon={faLinkedinIn} size="lg" color="#01C2F3" />
            </div>
            <div>
              <a
                href=" https://www.linkedin.com/in/ryanrayner/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://www.linkedin.com/in/ryanrayner/
              </a>
            </div>
          </div>
          <div className="flex-box-blk">
            <div>
              <Globe size={20} color="#01C2F3" />
            </div>
            <div>
              <a
                href="https://icxeed.ai/"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://icxeed.ai/
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CXPerspectives;
